import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAnnouncementApi,
  createAnnouncementApi,
  updateAnnouncementApi,
  deleteAnnouncementApi,
} from "../api/announcementApi";
import { extractApiError } from "../api/apiErrorHandler";
import { showToast } from "components/toast";

const initialState = {
  data: {},
  loading: true,
  error: null,
  // header data
  headerData: {},
  headerDataLoading: true,
  headerDataError: null,

  // Create
  createLoading: false,
  createError: null,

  // Edit
  editLoading: false,
  editError: null,

  // Delete
  deleteLoading: false,
  deleteError: null,
};

// 🔹 Get Announcements
export const getAnnouncements = createAsyncThunk(
  "announcements/getAnnouncements",
  async (data, thunkAPI) => {
    try {
      return await getAnnouncementApi(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);

// 🔹 Create Announcement
export const createAnnouncement = createAsyncThunk(
  "announcements/createAnnouncement",
  async (data, thunkAPI) => {
    try {
      return await createAnnouncementApi(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);

// 🔹 Update Announcement
export const updateAnnouncement = createAsyncThunk(
  "announcements/updateAnnouncement",
  async (data, thunkAPI) => {
    try {
      return await updateAnnouncementApi(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);

// 🔹 Delete Announcement
export const deleteAnnouncement = createAsyncThunk(
  "announcements/deleteAnnouncement",
  async (data, thunkAPI) => {
    try {
      return await deleteAnnouncementApi(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);

// 🔹 Get Announcements
export const getAnnouncementsHeader = createAsyncThunk(
  "announcements/getAnnouncementsHeader",
  async (data, thunkAPI) => {
    try {
      return await getAnnouncementApi(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(extractApiError(error));
    }
  }
);

const announcementSlice = createSlice({
  name: "announcements",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // 📥 FETCH ANNOUNCEMENTS
    builder
      .addCase(getAnnouncements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnnouncements.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.data = action.payload ?? {};
      })
      .addCase(getAnnouncements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // 📥 FETCH ANNOUNCEMENTS HEADER
    builder
      .addCase(getAnnouncementsHeader.pending, (state) => {
        state.headerDataLoading = true;
        state.headerDataError = null;
      })
      .addCase(getAnnouncementsHeader.fulfilled, (state, action) => {
        state.headerDataLoading = false;
        state.headerDataError = null;
        state.headerData = action.payload ?? {};
      })
      .addCase(getAnnouncementsHeader.rejected, (state, action) => {
        state.headerDataLoading = false;
        state.headerDataError = action.payload;
      });

    // ➕ CREATE ANNOUNCEMENT
    builder
      .addCase(createAnnouncement.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createAnnouncement.fulfilled, (state) => {
        state.createLoading = false;
        showToast.success("Announcement created successfully!");
      })
      .addCase(createAnnouncement.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload || "Failed to create announcement";
        showToast.error(state.createError);
      });

    // ✏️ UPDATE ANNOUNCEMENT
    builder
      .addCase(updateAnnouncement.pending, (state) => {
        state.editLoading = true;
        state.editError = null;
      })
      .addCase(updateAnnouncement.fulfilled, (state) => {
        state.editLoading = false;
        showToast.success("Announcement updated successfully!");
      })
      .addCase(updateAnnouncement.rejected, (state, action) => {
        state.editLoading = false;
        state.editError = action.payload || "Failed to update announcement";
        showToast.error(state.editError);
      });

    // 🗑️ DELETE ANNOUNCEMENT
    builder
      .addCase(deleteAnnouncement.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteAnnouncement.fulfilled, (state) => {
        state.deleteLoading = false;
        showToast.success("Announcement deleted successfully.");
      })
      .addCase(deleteAnnouncement.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload || "Failed to delete announcement";
        showToast.error(state.deleteError);
      });
  },
});

export default announcementSlice.reducer;
